import React from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { CtaButton } from '../CtaButton';
import { PageLightbox } from '../PageLightbox';
import { CoverflowStage } from './CoverflowStage';
import { SnapTrack } from './SnapTrack';
import { usePeekInside } from './usePeekInside';
import { peekInsideSlides } from '../../data/peekInsideSlides';
type PeekInsideProps = {
  autoplay?: boolean;
};
const arrowClass = 'flex h-11 w-11 flex-none items-center justify-center rounded-full border border-gold text-gold transition-colors duration-150 ease-out hover:bg-gold hover:text-gold-ink focus:outline-none focus-visible:ring-2 focus-visible:ring-gold focus-visible:ring-offset-2 focus-visible:ring-offset-plum-deep';
export function PeekInside({
  autoplay = true
}: PeekInsideProps) {
  const slides = peekInsideSlides;
  const c = usePeekInside(slides.length, autoplay);
  return <section aria-labelledby="peek-heading" className="w-full overflow-hidden bg-plum-deep" data-id="element-152" data-mp="1k2mc957z00sx">
      <div className="max-w-5xl mx-auto px-5 py-16 sm:py-20" data-id="element-153" data-mp="2zvdj9ocnjw20">
        <div className="text-center" data-id="element-154" data-mp="2uywucrxmpdnv">
          <p className="text-[11px] font-semibold uppercase tracking-[1.8px] text-gold" data-id="element-155" data-mp="2uxv95umwfasf">
            Volume 1 · First Seat
          </p>
          <h2 id="peek-heading" className="mx-auto mt-3 max-w-3xl font-display font-semibold text-white text-3xl leading-tight sm:text-5xl" data-id="element-156" data-mp="34o6osefy38f5">
            Peek inside the pages that get you{' '}
            <span className="text-gold" data-id="element-157" data-mp="2c0authhrfum2">booked again.</span>
          </h2>
          <p className="mx-auto mt-4 max-w-xl text-base text-white/75" data-id="element-158" data-mp="2pysg78k70v0t">
            10 table-ready one-shots, run sheets, table kits and pre-gens.
            Everything you need after a 20-minute read.
          </p>
        </div>

        <div role="region" aria-roledescription="carousel" aria-label="Volume 1 sample pages" tabIndex={0} onKeyDown={c.onKeyDown} {...c.pauseHandlers} className="relative mt-10 rounded-2xl focus:outline-none focus-visible:ring-2 focus-visible:ring-gold/60" data-id="element-159" data-mp="57rswif3ndao">
          {c.isDesktop ? <div className="flex items-center justify-between gap-4 py-4" data-id="element-160" data-mp="332mlmp25ybyj">
              <button type="button" onClick={c.prev} aria-label="Previous page" className={`${arrowClass} relative z-20`} data-id="element-161" data-mp="330jw064xakuc">
                <ChevronLeftIcon className="h-5 w-5" aria-hidden="true" data-id="element-162" data-mp="2bbqhqdy2zhne" />
              </button>
              <CoverflowStage slides={slides} active={c.active} reduceMotion={c.reduceMotion} onSelect={c.goTo} onOpen={c.openLightbox} data-id="element-163" data-mp="2fcipt3v7vgi3" />
              <button type="button" onClick={c.next} aria-label="Next page" className={`${arrowClass} relative z-20`} data-id="element-164" data-mp="2p3desfzuku78">
                <ChevronRightIcon className="h-5 w-5" aria-hidden="true" data-id="element-165" data-mp="1rmxurmlsgssg" />
              </button>
            </div> : <div className="-mx-5" data-id="element-166" data-mp="1p2rr7g1374cf">
              <SnapTrack slides={slides} active={c.active} trackRef={c.trackRef} onScroll={c.onTrackScroll} onOpen={c.openLightbox} data-id="element-167" data-mp="1p1qg5bk2mhup" />
            </div>}

          <div className="mx-auto mt-5 min-h-[3rem] max-w-md text-center" aria-live="polite" data-id="element-168" data-mp="1f6gamdugrsc6">
            <AnimatePresence mode="wait" initial={false} data-id="element-169" data-mp="1b5cwpwvz86dl">
              <motion.p key={c.active} initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} exit={{
              opacity: 0
            }} transition={{
              duration: c.reduceMotion ? 0 : 0.15,
              ease: 'easeOut'
            }} className="font-display text-lg italic leading-snug text-white/85" data-id="element-170" data-mp="164p7ngrb5zm0">
                {slides[c.active].caption}
              </motion.p>
            </AnimatePresence>
          </div>

          <div className="mt-4 flex items-center justify-center gap-4" data-id="element-171" data-mp="idtcc9nn8ma0">
            {!c.isDesktop && <button type="button" onClick={c.prev} aria-label="Previous page" className={arrowClass} data-id="element-172" data-mp="2igccplzjcxz6">
                <ChevronLeftIcon className="h-5 w-5" aria-hidden="true" data-id="element-173" data-mp="2mh4l6d0l2yue" />
              </button>}
            <div className="flex items-center gap-1" role="group" aria-label="Choose a page" data-id="element-174" data-mp="28pztbx3gjaum">
              {slides.map((slide, i) => <button key={slide.src} type="button" onClick={() => c.goTo(i)} aria-label={`Go to page ${i + 1}`} aria-current={i === c.active ? 'true' : undefined} className="group flex h-8 w-8 items-center justify-center rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-gold" data-id="element-175" data-mp="2dnj76vw8tlm1" data-mpi={i}>
                  <span className={`block h-2 rounded-full transition-[width,background-color] duration-200 ease-out ${i === c.active ? 'w-6 bg-gold' : 'w-2 bg-white/30 group-hover:bg-white/60'}`} data-id="element-176" data-mp="3k5epwzrr80p7" />
                </button>)}
            </div>
            {!c.isDesktop && <button type="button" onClick={c.next} aria-label="Next page" className={arrowClass} data-id="element-177" data-mp="3u81ug0joe9eo">
                <ChevronRightIcon className="h-5 w-5" aria-hidden="true" data-id="element-178" data-mp="bxqb0irr56ev" />
              </button>}
          </div>
        </div>

        <div className="mx-auto mt-10 w-full max-w-md" data-id="element-179" data-mp="lta1uqrglewr">
          <CtaButton data-id="element-180" data-mp="lvw9eqecnuez">Get Paid Table Ready</CtaButton>
        </div>
      </div>

      <PageLightbox pages={slides} index={c.lightboxIndex} onClose={c.closeLightbox} onChange={c.changeLightbox} data-id="element-181" data-mp="guqgargs254x" />
    </section>;
}